import React, { useMemo } from 'react';

const ICONS = {
  heart : (color) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill={color} style={{ animation: 'heartbeat 1.2s ease infinite' }}>
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
    </svg>
  ),
  drop  : (color) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill={color}>
      <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/>
    </svg>
  ),
  temp  : (color) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
      <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"/>
    </svg>
  ),
  motion: (color) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
  ),
};

const STATUS = {
  ok      : { color: '#059669', bg: '#ecfdf5', border: '#a7f3d0', ring: '#10b981', label: 'Normal'   },
  warning : { color: '#d97706', bg: '#fffbeb', border: '#fde68a', ring: '#f59e0b', label: 'Warning'  },
  critical: { color: '#dc2626', bg: '#fff1f2', border: '#fecaca', ring: '#ef4444', label: 'Critical' },
  motion  : { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', ring: '#3b82f6', label: 'Motion'   },
};

// Arc progress indicator (SVG)
function Arc({ value, min, max, color }) {
  const pct = Math.min(1, Math.max(0, (value - min) / (max - min)));
  const r = 26, cx = 32, cy = 32;
  const circ = 2 * Math.PI * r;
  const dash = pct * circ * 0.75;
  const gap  = circ - dash;
  const rot  = -225; // start at bottom-left
  return (
    <svg width="64" height="64" style={{ position: 'absolute', top: 0, right: 0 }}>
      {/* Track */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f3f4f6" strokeWidth="5"
        strokeDasharray={`${circ * 0.75} ${circ * 0.25}`}
        strokeDashoffset={0}
        strokeLinecap="round"
        transform={`rotate(${rot} ${cx} ${cy})`}
      />
      {/* Fill */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth="5"
        strokeDasharray={`${dash} ${gap + circ * 0.25}`}
        strokeLinecap="round"
        transform={`rotate(${rot} ${cx} ${cy})`}
        style={{ transition: 'stroke-dasharray .6s ease', filter: `drop-shadow(0 0 4px ${color}66)` }}
      />
    </svg>
  );
}

export default function VitalCard({ icon, label, value, unit, status = 'ok', sub, min, max }) {
  const cfg = STATUS[status] ?? STATUS.ok;
  const numeric = parseFloat(value);
  const hasArc = value != null && !isNaN(numeric) && min != null && max != null;

  return (
    <div style={{ ...s.card, borderColor: cfg.border, background: `linear-gradient(145deg, rgba(255,255,255,0.9), ${cfg.bg}88)` }}>
      {/* Arc */}
      {hasArc && <Arc value={numeric} min={min} max={max} color={cfg.ring} />}

      {/* Icon */}
      <div style={{ ...s.iconWrap, background: cfg.bg, border: `1px solid ${cfg.border}` }}>
        {ICONS[icon]?.(cfg.color)}
      </div>

      {/* Label */}
      <div style={s.label}>{label}</div>

      {/* Value */}
      <div style={s.valueRow}>
        {value != null
          ? <span style={{ ...s.value, color: cfg.color }}>{value}</span>
          : <span style={s.valuePlaceholder}>—</span>
        }
        {value != null && <span style={{ ...s.unit, color: cfg.color + 'cc' }}>{unit}</span>}
      </div>

      {/* Status pill */}
      <div style={{ ...s.pill, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
        <span style={{ ...s.pillDot, background: cfg.ring }} />
        {sub || cfg.label}
      </div>
    </div>
  );
}

const s = {
  card: {
    position        : 'relative',
    backdropFilter  : 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius    : 20,
    border          : '1px solid',
    boxShadow       : '0 4px 24px rgba(99,102,241,0.07), 0 1px 0 rgba(255,255,255,0.9) inset',
    padding         : '20px 18px 16px',
    display         : 'flex',
    flexDirection   : 'column',
    gap             : 8,
    overflow        : 'hidden',
    transition      : 'transform .15s ease, box-shadow .15s ease',
  },
  iconWrap : {
    width       : 40,
    height      : 40,
    borderRadius: 12,
    display     : 'flex',
    alignItems  : 'center',
    justifyContent: 'center',
    flexShrink  : 0,
  },
  label   : { fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em', marginTop: 2 },
  valueRow: { display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 2 },
  value   : { fontSize: 36, fontWeight: 800, lineHeight: 1, letterSpacing: '-1px' },
  valuePlaceholder: { fontSize: 36, fontWeight: 800, color: '#d1d5db', lineHeight: 1 },
  unit    : { fontSize: 14, fontWeight: 600, marginBottom: 2 },
  pill    : {
    display     : 'inline-flex',
    alignItems  : 'center',
    gap         : 5,
    fontSize    : 11,
    fontWeight  : 600,
    padding     : '4px 10px',
    borderRadius: 99,
    marginTop   : 4,
    width       : 'fit-content',
  },
  pillDot : { width: 6, height: 6, borderRadius: '50%', flexShrink: 0 },
};
